import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Typography,
  Chip,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { s3BucketsApi, S3BucketConfig, S3BucketEnvironment } from '../services/s3-buckets-api';
import { moodleApi } from '../services/moodle-api';

interface CMI5Activity {
  tenantId: string;
  courseId: string;
  activityId: number;
  activityName: string;
  cmi5activityid?: string;
  moodleCourseId?: number;
}

interface S3BatchExportDialogProps {
  open: boolean;
  onClose: () => void;
  sourceId: string;
  activities: CMI5Activity[];
}

interface ExportResult {
  activity: CMI5Activity;
  status: 'pending' | 'exporting' | 'success' | 'error';
  s3Key?: string;
  error?: string;
}

export const S3BatchExportDialog: React.FC<S3BatchExportDialogProps> = ({
  open,
  onClose,
  sourceId,
  activities,
}) => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [buckets, setBuckets] = useState<S3BucketConfig[]>([]);
  const [environments, setEnvironments] = useState<S3BucketEnvironment[]>([]);
  const [selectedBucketId, setSelectedBucketId] = useState<string>('');
  const [selectedEnvId, setSelectedEnvId] = useState<string>('');

  const [exportResults, setExportResults] = useState<ExportResult[]>([]);
  const [currentExportIndex, setCurrentExportIndex] = useState<number>(-1);

  useEffect(() => {
    if (open) {
      loadS3Configurations();
      setError(null);
      setExportResults(activities.map(activity => ({ activity, status: 'pending' })));
      setCurrentExportIndex(-1);
    }
  }, [open, activities]);

  const loadS3Configurations = async () => {
    setLoading(true);
    setError(null);
    try {
      const [bucketsData, environmentsData] = await Promise.all([
        s3BucketsApi.listBuckets(),
        s3BucketsApi.listEnvironments(),
      ]);
      setBuckets(bucketsData);
      setEnvironments(environmentsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load S3 configurations');
    } finally {
      setLoading(false);
    }
  };

  const filteredEnvironments = environments.filter(
    env => !selectedBucketId || env.bucketId === selectedBucketId
  );

  const handleExportAll = async () => {
    if (!selectedBucketId || !selectedEnvId) {
      setError('Please select both a bucket and an environment');
      return;
    }

    setExporting(true);
    setError(null);

    // Export each activity sequentially
    for (let i = 0; i < activities.length; i++) {
      const activity = activities[i];
      setCurrentExportIndex(i);

      // Update status to exporting
      setExportResults(prev =>
        prev.map((result, idx) =>
          idx === i ? { ...result, status: 'exporting' } : result
        )
      );

      try {
        const result = await moodleApi.exportCMI5ActivityToS3(
          sourceId,
          activity.tenantId,
          activity.courseId,
          selectedBucketId,
          selectedEnvId,
          activity.activityName,
          activity.cmi5activityid,
          activity.moodleCourseId
        );

        // Update status to success
        setExportResults(prev =>
          prev.map((result, idx) =>
            idx === i ? { ...result, status: 'success', s3Key: result.s3Key } : result
          )
        );
      } catch (err) {
        // Update status to error
        setExportResults(prev =>
          prev.map((result, idx) =>
            idx === i ? {
              ...result,
              status: 'error',
              error: err instanceof Error ? err.message : 'Export failed'
            } : result
          )
        );
      }
    }

    setExporting(false);
    setCurrentExportIndex(-1);
  };

  const handleClose = () => {
    if (!exporting) {
      onClose();
    }
  };

  const successCount = exportResults.filter(r => r.status === 'success').length;
  const errorCount = exportResults.filter(r => r.status === 'error').length;
  const allComplete = exportResults.length > 0 && exportResults.every(r => r.status === 'success' || r.status === 'error');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Export {activities.length} CMI5 {activities.length === 1 ? 'Activity' : 'Activities'} to S3
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {exporting && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                Exporting activity {currentExportIndex + 1} of {activities.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {successCount} success, {errorCount} failed
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(currentExportIndex + 1) / activities.length * 100}
            />
          </Box>
        )}

        {allComplete && (
          <Alert severity={errorCount === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {errorCount === 0
              ? `Successfully exported all ${successCount} activities!`
              : `Exported ${successCount} of ${activities.length} activities. ${errorCount} failed.`
            }
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth disabled={exporting}>
              <InputLabel>S3 Bucket</InputLabel>
              <Select
                value={selectedBucketId}
                onChange={(e) => {
                  setSelectedBucketId(e.target.value);
                  setSelectedEnvId(''); // Reset environment selection
                }}
                label="S3 Bucket"
              >
                {buckets.map((bucket) => (
                  <MenuItem key={bucket.id} value={bucket.id}>
                    {bucket.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedBucketId || exporting}>
              <InputLabel>Environment</InputLabel>
              <Select
                value={selectedEnvId}
                onChange={(e) => setSelectedEnvId(e.target.value)}
                label="Environment"
              >
                {filteredEnvironments.map((env) => (
                  <MenuItem key={env.id} value={env.id}>
                    {env.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Activities to Export:
              </Typography>
              <List dense sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {exportResults.map((result, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      result.status === 'success' ? (
                        <Chip icon={<CheckCircleIcon />} label="Success" color="success" size="small" />
                      ) : result.status === 'error' ? (
                        <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
                      ) : result.status === 'exporting' ? (
                        <CircularProgress size={20} />
                      ) : null
                    }
                  >
                    <ListItemText
                      primary={result.activity.activityName}
                      secondary={
                        result.status === 'error'
                          ? result.error
                          : result.status === 'success'
                          ? `Exported to ${result.s3Key}`
                          : `Tenant: ${result.activity.tenantId}, Course: ${result.activity.courseId}`
                      }
                      secondaryTypographyProps={{
                        color: result.status === 'error' ? 'error' : 'text.secondary'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Alert severity="info" sx={{ fontSize: '0.875rem', mt: 1 }}>
              This will extract each CMI5 activity from the Moodle pod and upload them to the selected S3 bucket with metadata preserved.
              Activities will be exported sequentially.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={exporting}>
          {allComplete ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handleExportAll}
          variant="contained"
          disabled={!selectedBucketId || !selectedEnvId || exporting || allComplete}
        >
          {exporting ? <CircularProgress size={20} /> : `Export ${activities.length} ${activities.length === 1 ? 'Activity' : 'Activities'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
